import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import {
  MAX_INJURY_NOTE_LENGTH,
  type PainTrend,
} from "@repo/contract/training";
import {
  "bodymap.age.label" as ageLabel,
  "profile-editors.pain_answer_required" as answerRequired,
  "profile-editors.pain_disclaimer" as disclaimerText,
  "profile-editors.pain_intensity_group" as intensityGroup,
  "profile-editors.pain_intensity" as intensityLabel,
  "profile-editors.pain_intensity_meta" as intensityMeta,
  "profile-editors.pain_intensity_option" as intensityOption,
  "profile-editors.pain_intensity_mild" as mildText,
  "profile-editors.pain_intensity_moderate" as moderateText,
  "bodymap.nature.label" as natureLabel,
  "profile-editors.pain_note_error" as noteError,
  "profile-editors.pain_note" as noteLabel,
  "profile-editors.pain_note_placeholder" as notePlaceholder,
  "profile-editors.pain_occurrence_current" as occurrenceCurrent,
  "profile-editors.pain_occurrence_historical" as occurrenceHistorical,
  "profile-editors.pain_occurrence" as occurrenceLabel,
  "profile-editors.pain_title" as painTitle,
  "profile-editors.pain_region_required" as regionRequired,
  "profile-editors.pain_side_both" as sideBoth,
  "profile-editors.pain_side" as sideLabel,
  "profile-editors.pain_side_left" as sideLeft,
  "profile-editors.pain_side_right" as sideRight,
  "profile-editors.pain_since" as sinceLabel,
  "bodymap.status.label" as statusLabel,
  "profile-editors.pain_intensity_strong" as strongText,
  "profile-editors.pain_trend" as trendLabel,
  "bodymap.type.label" as typeLabel,
  "bodymap.when.label" as whenLabel,
} from "@repo/i18n";
import { Check } from "lucide-react";
import { useCallback, useState } from "react";
import { cx } from "../../../lib/cx";
import { Icon, Segmented, TextField } from "../../../ui/controls";
import {
  injuryAgeOptions,
  injuryStatusOptions,
  injuryTypeOptions,
  type LabelledOption,
  momentOptions,
  natureOptions,
  painAreaLabel,
  sinceOptions,
  trendOptions,
} from "./options";
import {
  intensityLevel,
  isPaired,
  PAIN_AREAS,
  type PainArea,
  type PainForm as PainDraft,
  type PainSide,
  type SinceBucket,
  sinceBucketOf,
  sinceDayOf,
} from "./pain-model";
import { FieldError, FieldLabel, InfoNote } from "./parts";
import type { FieldErrors } from "./validate";

const LEVEL_WORD = {
  mild: mildText,
  moderate: moderateText,
  strong: strongText,
} as const;

export interface PainFormProps {
  errors?: FieldErrors;
  onChange: (value: PainDraft) => void;
  value: PainDraft;
}

type Occurrence = PainDraft["occurrence"];

/**
 * A2 `PainSheet` (editors.tsx:941-1085) over the contract's injury report:
 * where (area, then side for a paired joint), whether it hurts now or is a
 * past injury, that occurrence's questions, and a free-text note. The other
 * occurrence's answers are kept in the draft and never sent (`pain-model.ts`).
 *
 * "Desde quando?" is drawn from the draft's stored DAY and writes a day back,
 * so the chips are a view of `since` rather than the thing being edited; and
 * "Como está evoluindo?" is asked only of a pain that already exists, as A2
 * asked it (editors.tsx:1046). Neither appears for a historical injury.
 */
export function PainForm({ value, onChange, errors }: PainFormProps) {
  // One instant for the life of the sheet: `new Date()` in render would give
  // the bucket a different "today" on every keystroke elsewhere in the form.
  const [now] = useState(() => new Date());
  const errorOf = (field: string) =>
    errors?.[field] === undefined ? undefined : answerRequired();

  const set = useCallback(
    <K extends keyof PainDraft>(key: K) =>
      (next: PainDraft[K]) =>
        onChange({ ...value, [key]: next }),
    [onChange, value]
  );
  const setArea = useCallback(
    (area: PainArea) => onChange({ ...value, area }),
    [onChange, value]
  );
  const setSide = useCallback(
    (side: PainSide) => onChange({ ...value, side }),
    [onChange, value]
  );
  const setOccurrence = useCallback(
    (occurrence: Occurrence) => onChange({ ...value, occurrence }),
    [onChange, value]
  );
  const setNote = useCallback(
    (note: string) => onChange({ ...value, note }),
    [onChange, value]
  );
  const setIntensity = useCallback(
    (intensity: string) => onChange({ ...value, intensity: Number(intensity) }),
    [onChange, value]
  );
  const setSince = useCallback(
    (bucket: SinceBucket) =>
      onChange({ ...value, sinceDay: sinceDayOf(bucket, now) }),
    [now, onChange, value]
  );
  const setTrend = useCallback(
    (trend: PainTrend) => onChange({ ...value, trend }),
    [onChange, value]
  );
  const sinceBucket =
    value.sinceDay === null ? null : sinceBucketOf(value.sinceDay, now);

  return (
    <div>
      <SingleChips
        label={painTitle()}
        onChange={setArea}
        options={PAIN_AREAS.map((area) => ({
          label: painAreaLabel(area),
          value: area,
        }))}
        value={value.area}
      />
      {errors?.region === undefined ? null : (
        <FieldError>{regionRequired()}</FieldError>
      )}

      {value.area !== null && isPaired(value.area) ? (
        <div className="mt-4">
          <Segmented<PainSide>
            label={sideLabel()}
            onValueChange={setSide}
            options={[
              { label: sideRight(), value: "right" },
              { label: sideLeft(), value: "left" },
              { label: sideBoth(), value: "both" },
            ]}
            value={value.side}
          />
        </div>
      ) : null}

      <div className="mt-6">
        <FieldLabel>{occurrenceLabel()}</FieldLabel>
        <Segmented<Occurrence>
          label={occurrenceLabel()}
          onValueChange={setOccurrence}
          options={[
            { label: occurrenceCurrent(), value: "current" },
            { label: occurrenceHistorical(), value: "historical" },
          ]}
          value={value.occurrence}
        />
      </div>

      {value.occurrence === "current" ? (
        <>
          <Question
            error={errorOf("nature")}
            label={natureLabel()}
            onChange={set("nature")}
            options={natureOptions()}
            value={value.nature}
          />
          <Question
            error={errorOf("when")}
            label={whenLabel()}
            onChange={set("when")}
            options={momentOptions()}
            value={value.when}
          />
          <Question
            error={errorOf("since")}
            label={sinceLabel()}
            onChange={setSince}
            options={sinceOptions()}
            value={sinceBucket}
          />
          <div className="mt-6">
            <FieldLabel
              meta={intensityMeta({
                n: value.intensity,
                word: LEVEL_WORD[intensityLevel(value.intensity)](),
              })}
            >
              {intensityLabel()}
            </FieldLabel>
            <IntensityScale onChange={setIntensity} value={value.intensity} />
          </div>
          {value.existing ? (
            <div className="mt-6">
              <FieldLabel>{trendLabel()}</FieldLabel>
              <Segmented<PainTrend>
                label={trendLabel()}
                onValueChange={setTrend}
                options={trendOptions()}
                value={value.trend ?? "same"}
              />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <Question
            error={errorOf("type")}
            label={typeLabel()}
            onChange={set("type")}
            options={injuryTypeOptions()}
            value={value.type}
          />
          <Question
            error={errorOf("status")}
            label={statusLabel()}
            onChange={set("status")}
            options={injuryStatusOptions()}
            value={value.status}
          />
          <Question
            error={errorOf("howLongAgo")}
            label={ageLabel()}
            onChange={set("howLongAgo")}
            options={injuryAgeOptions()}
            value={value.howLongAgo}
          />
        </>
      )}

      <div className="mt-6">
        <TextField
          error={
            value.note.trim().length > MAX_INJURY_NOTE_LENGTH
              ? noteError()
              : undefined
          }
          label={noteLabel()}
          maxLength={MAX_INJURY_NOTE_LENGTH}
          onValueChange={setNote}
          placeholder={notePlaceholder()}
          value={value.note}
        />
      </div>

      <InfoNote boxed>{disclaimerText()}</InfoNote>
    </div>
  );
}

function Question<T extends string>({
  label,
  options,
  value,
  onChange,
  error,
}: {
  error?: string;
  label: string;
  onChange: (value: T) => void;
  options: readonly LabelledOption<T>[];
  value: T | null;
}) {
  return (
    <div className="mt-6">
      <FieldLabel>{label}</FieldLabel>
      <SingleChips
        label={label}
        onChange={onChange}
        options={options}
        value={value}
      />
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

/**
 * A2's `SingleChips` (editors.tsx:913-937): one answer, drawn as chips, on
 * Base UI's RadioGroup so the group is one tab stop and the arrows move.
 */
export function SingleChips<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  onChange: (value: T) => void;
  options: readonly LabelledOption<T>[];
  value: T | null;
}) {
  const select = useCallback(
    (next: T | null) => {
      if (next !== null) {
        onChange(next);
      }
    },
    [onChange]
  );
  return (
    <RadioGroup<T | null>
      aria-label={label}
      className="flex flex-wrap gap-2"
      onValueChange={select}
      value={value}
    >
      {options.map((option) => (
        <Radio.Root
          className="group a-press inline-flex h-12 cursor-pointer select-none items-center gap-1.5 rounded-full bg-surface px-4 font-medium text-base text-ink-2 ring-1 ring-line transition-colors hover:ring-line-strong data-checked:bg-inverse data-checked:text-on-inverse data-checked:ring-0"
          key={option.value}
          value={option.value}
        >
          <Radio.Indicator className="-ml-1 flex">
            <Icon bold icon={Check} size={16} />
          </Radio.Indicator>
          {option.label}
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}

/** 1 to 10, filled up to the answer (A2 editors.tsx:1024-1044). */
function IntensityScale({
  value,
  onChange,
}: {
  onChange: (value: string) => void;
  value: number;
}) {
  const select = useCallback(
    (next: string | null) => {
      if (next !== null) {
        onChange(next);
      }
    },
    [onChange]
  );
  return (
    <RadioGroup<string | null>
      aria-label={intensityGroup()}
      className="grid grid-cols-10 gap-1"
      onValueChange={select}
      value={String(value)}
    >
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <Radio.Root
          aria-label={intensityOption({ n })}
          className={cx(
            "h-12 cursor-pointer rounded-md font-semibold text-sm tabular-nums transition-colors",
            n <= value
              ? "bg-accent text-on-accent"
              : "bg-surface text-ink-3 ring-1 ring-line"
          )}
          key={n}
          value={String(n)}
        >
          {n}
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}
